/**
 * @deprecated Use `NewConfirmationModal` from `~/components/NewConfirmationModal` instead.
 */
import {
  ButtonProps,
  Divider,
  Heading,
  ModalContentProps,
  ModalProps,
} from '@chakra-ui/react';
import * as React from 'react';
import { useMountedState } from 'react-use';
import { useSnackbar } from 'ui-components';
import Button from '~/components/Button';
import LinkButton from '~/components/LinkButton';
import Modal from '~/components/Modal/Modal';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { captureException } from '~/errorsLogging';

/**
 * @deprecated Use `NewConfirmationModal` from `~/components/NewConfirmationModal` instead.
 */
interface Props extends Omit<ModalProps, 'onClose'> {
  modalContentProps?: ModalContentProps;
  cancelButtonProps?: ButtonProps;
  cancelButtonText?: string;
  cancelButtonVariant?: string;
  children: React.ReactChild[] | React.ReactChild;
  confirmButtonLoadingText?: string;
  confirmButtonProps?: ButtonProps;
  confirmButtonText?: string;
  confirmButtonVariant?: string;
  inverted?: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onClose?: () => void;
  title?: string;
  showHeaderDivider?: boolean;
  showFooterDivider?: boolean;
}

/**
 * @deprecated Use `NewConfirmationModal` from `~/components/NewConfirmationModal` instead.
 */
const ConfirmationModal: React.FC<Props> = ({
  modalContentProps = {},
  cancelButtonProps = {},
  cancelButtonText = 'Cancel',
  cancelButtonVariant = 'primary',
  children,
  confirmButtonLoadingText = 'Submitting...',
  confirmButtonProps = {},
  confirmButtonText = 'Confirm',
  confirmButtonVariant = 'error',
  inverted = false,
  title = '',
  isOpen,
  onCancel,
  onClose,
  onConfirm,
  showHeaderDivider = true,
  showFooterDivider = false,
  ...rest
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMounted = useMountedState();
  const snackbar = useSnackbar();

  const ConfirmButton = inverted ? LinkButton : Button;
  const defaultConfirmButtonProps = inverted
    ? { color: confirmButtonVariant }
    : { colorScheme: confirmButtonVariant };
  const CancelButton = inverted ? Button : LinkButton;
  const defaultCancelButtonProps = inverted
    ? { colorScheme: cancelButtonVariant }
    : { color: cancelButtonVariant };

  const handleConfirmClick = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
    if (isMounted()) {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      modalContentProps={modalContentProps}
      {...rest}
      onClose={onClose || onCancel}
    >
      <Modal.Header>
        <Heading as="h1" fontWeight="semibold" fontSize="2xl" marginX={2}>
          {title}
        </Heading>
      </Modal.Header>
      <Modal.CloseButton marginX={3} marginY={1} />

      {showHeaderDivider && <Divider width="auto" marginX={7} marginY={0} />}

      <Modal.Body>{children}</Modal.Body>

      {showFooterDivider && <Divider width="auto" marginX={3} marginY={0} />}

      <Modal.Footer
        spaceBetween
        flexDirection={inverted ? 'row' : 'row-reverse'}
        showDivider={false}
      >
        <ConfirmButton
          data-testid="confirmation-modal-confirm"
          isLoading={isSubmitting}
          loadingText={confirmButtonLoadingText}
          {...defaultConfirmButtonProps}
          {...confirmButtonProps}
          onClick={handleConfirmClick}
        >
          {confirmButtonText}
        </ConfirmButton>

        <CancelButton
          {...defaultCancelButtonProps}
          {...cancelButtonProps}
          onClick={onCancel}
        >
          {cancelButtonText}
        </CancelButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationModal;
